import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Spinner, Input, Button, Badge, Alert } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../helpers/api';
import FeatherIcon from 'feather-icons-react';
import { handleApiResponse, createNotificationFromApiResponse, formatCreatedDate, formatDateTimeForAPI, workstatusOptions } from '../constants/utils';
import AssetUsageStatusFields from '../components/AssetUsageStatusFields';
import WorkOrderDetailsModal from '../components/WorkOrderDetailsModal';
import AssetModal from '../components/AssetModal';
import { getPresignedUrl } from '../helpers/fileUpload';

// Import new hooks and components
import { useReferenceData, usePagination, useNotification, useModalState } from '../Hooks/index.js'
import { NotificationAlert, LoadMoreButton, ConfirmDeleteModal } from '../components/Common/index.js';
import PermissionGuard from '../components/Common/PermissionGuard';
import { useTranslation } from 'react-i18next';

const Recall = () => {
    const { t } = useTranslation();
    document.title = `${t("shared.recall", "Recall")} | ArcMedix`;

    // User context
    const user = JSON.parse(localStorage.getItem('authUser'));
    const organizationId = user?.activeOrganizationId ?? null;

    // State
    const [recallRequests, setRecallRequests] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');


    // Asset selection state
    const [assets, setAssets] = useState([]);
    const [assetSearchTerm, setAssetSearchTerm] = useState("");
    const [showAssetDropdown, setShowAssetDropdown] = useState(false);
    const [selectedAssetDisplay, setSelectedAssetDisplay] = useState("");
    const [assetFilterModal, setAssetFilterModal] = useState(false);
    const [assetFilters, setAssetFilters] = useState({
        assetTypeId: '',
        location: '',
        manufacturer: ''
    });
    const [assetTypes, setAssetTypes] = useState([]);

    // Image/Document modal state
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [currentImages, setCurrentImages] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [currentDocuments, setCurrentDocuments] = useState([]);

    // Asset modal state
    const [assetModal, setAssetModal] = useState({
        isOpen: false,
        userData: null,
        loading: false
    });

    // Use new hooks
    const { locations, departments, manufacturers, technicians } = useReferenceData(organizationId, {
        fetchLocations: true,
        fetchDepartments: true,
        fetchManufacturers: true,
        fetchTechnicians: true,
        fetchTools: false
    });

    const { count, hasMore, setHasMore, handleLoadMore: onLoadMore } = usePagination(10, 10);

    const { notification, showSuccess, showError } = useNotification(5000);

    const addEditModal = useModalState();
    const deleteModal = useModalState();
    const workOrderModal = useModalState();

    const fetchAssetTypes = async () => {
        try {
            const { data } = await api.get(`organization-asset-type/getAll?organizationId=${organizationId}`);
            setAssetTypes(data.response || []);
        } catch (error) {
            console.error('Error fetching asset types:', error);
            setAssetTypes([]);
        }
    };

    const fetchRecallRequests = async () => {
        try {
            setLoading(true);
            const params = {
                organizationId: organizationId || 1,
                count: count
            };
            if (searchTerm) {
                params.search = searchTerm;
            }
            const { data } = await api.get('recall/getAllrecall', { params });
            setRecallRequests(data?.response || []);
            setHasMore((data?.response?.length || 0) === count);
        } catch (error) {
            console.error('Error fetching recall requests:', error);
            setApiError(t("business.recall.failed_fetch", "Failed to fetch recall requests data"));
            setRecallRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssets = async (searchTerm = '') => {
        try {
            const params = {
                organizationId: organizationId,
                count: 9999,
                search: searchTerm,
                currentLocation: assetFilters.location,
                assetTypeId: assetFilters.assetTypeId,
                manufacturer: assetFilters.manufacturer,
                hierarchy: false
            };
            const { data } = await api.get(`asset/all`, { params });
            setAssets(data?.response[0]?.assets || []);
        } catch (error) {
            console.error('Error fetching assets:', error);
            setAssets([]);
        }
    };


    // Search handler
    const handleSearch = useCallback((e) => {
        setSearchTerm(e.target.value);
    }, []);

    useEffect(() => {
        fetchAssetTypes();
    }, []);

    // Effect to fetch data when count changes
    useEffect(() => {
        fetchRecallRequests();
    }, [count]);

    // Debounced search effect
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            fetchRecallRequests();
        }, 300); // 300ms delay

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    // Effect to fetch assets when search term or filters change
    useEffect(() => {
        if (organizationId && assetSearchTerm.length > 0) {
            fetchAssets(assetSearchTerm);
        } else if (organizationId && assetSearchTerm.length === 0) {
            setAssets([]);
        }
    }, [organizationId, assetSearchTerm, assetFilters]);

    const handleAdd = () => {
        setSelectedEntry({
            title: '',
            assetId: '',
            assignedTechnicianId: '',
            description: '',
            status: 'SUBMITTED',
            recallImageUrls: [],
            recallDocumentUrls: [],
            usageStatus: '',
            downtimeReasonId: null
        });
        setErrors({});
        setApiError('');
        setAssetSearchTerm("");
        setSelectedAssetDisplay("");
        addEditModal.open();
    };

    const handleFileChange = async (e, type = 'images') => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        try {
            const uploadedUrls = [];

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const publicURL = await getPresignedUrl(file);
                if (publicURL) {
                    uploadedUrls.push(publicURL);
                }
            }

            if (type === 'images') {
                setSelectedEntry(prev => ({
                    ...prev,
                    recallImageUrls: [...(prev?.recallImageUrls || []), ...uploadedUrls]
                }));
            } else if (type === 'documents') {
                setSelectedEntry(prev => ({
                    ...prev,
                    recallDocumentUrls: [...(prev?.recallDocumentUrls || []), ...uploadedUrls]
                }));
            }

        } catch (err) {
            setApiError(t("shared.error_during_upload", "❌ Error during upload:") + " " + err.message);
        }
    };

    const removeImage = (indexToRemove) => {
        setSelectedEntry(prev => ({
            ...prev,
            recallImageUrls: prev?.recallImageUrls?.filter((_, index) => index !== indexToRemove) || []
        }));
    };

    const removeDocument = (indexToRemove) => {
        setSelectedEntry(prev => ({
            ...prev,
            recallDocumentUrls: prev?.recallDocumentUrls?.filter((_, index) => index !== indexToRemove) || []
        }));
    };

    const handleEdit = (entry) => {
        // Handle both assetId and asset properties
        const assetIdValue = entry.assetId?.id || entry.asset?.id || entry.assetId || '';
        const technicianIdValue = entry.assignedTechnicianId?.id || entry.assignedTechnician?.id || entry.assignedTechnicianId || '';

        setSelectedEntry({
            ...entry,
            title: entry.title || '',
            asset: entry.asset || entry.assetId || null,
            assetId: assetIdValue,
            assignedTechnicianId: technicianIdValue,
            description: entry.description || '',
            status: entry.status || 'SUBMITTED',
            recallImageUrls: entry.recallImageUrls || [],
            recallDocumentUrls: entry.recallDocumentUrls || [],
            usageStatus: entry.usageStatus || '',
            downtimeReasonId: entry.downtimeReasonId || null
        });
        // If editing, show the selected asset info
        if (entry.asset) {
            setSelectedAssetDisplay(`${entry.asset.assetName} - ${entry.asset.serialNumber}`);
        } else if (entry.assetId?.assetName) {
            setSelectedAssetDisplay(`${entry.assetId.assetName} - ${entry.assetId.serialNumber}`);
        } else {
            setSelectedAssetDisplay("");
        }
        setAssetSearchTerm("");
        setErrors({});
        setApiError('');
        addEditModal.open();
    };

    const handleAssetSelect = (asset) => {
        setSelectedEntry({
            ...selectedEntry,
            asset: asset,
            assetId: asset.id,
            usageStatus: asset.assetStatus || ''
        });
        setSelectedAssetDisplay(`${asset.assetName} - ${asset.serialNumber}`);
        setAssetSearchTerm("");
        setShowAssetDropdown(false);
        // Clear any existing error for assetId
        if (errors.assetId) {
            setErrors(prev => ({...prev, assetId: ''}));
        }
    };

    const handleAssetSearchChange = (e) => {
        const value = e.target.value;
        setAssetSearchTerm(value);
        setShowAssetDropdown(value.length > 0);
        if (value.length > 0) {
            fetchAssets(value);
        }
        if (value.length === 0) {
            setSelectedEntry({
                ...selectedEntry,
                asset: null,
                assetId: '',
                usageStatus: ''
            });
            setSelectedAssetDisplay("");
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.data) return;

        try {
            await api.delete(`recall/deleteRecall?id=${deleteModal.data?.id}`);
            showSuccess(t("business.recall.deleted_successfully", "Recall request deleted successfully"));
            fetchRecallRequests();
            deleteModal.close();
        } catch (error) {
            console.error('Error deleting recall request:', error);
            showError(t("business.recall.failed_delete", "Failed to delete recall request"));
        }
    };

    const handleWorkOrderClick = (workOrder) => {
        workOrderModal.open({ ...workOrder, type: 'recall' });
    };

    const handleAssetClick = async (assetId, cachedAsset = null) => {
        if (!assetId) return;

        setAssetModal(prev => ({
            ...prev,
            isOpen: true,
            userData: null,
            loading: true
        }));

        try {
            const { data: assetData } = await api.get(`asset/get-by-id?id=${assetId}`);
            const params = {
                assetId: assetId,
                count: 5
            };
            const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, { params });

            setAssetModal(prev => ({
                ...prev,
                userData: {
                    ...assetData.response,
                    recentTickets: issuesData?.response || []
                },
                loading: false
            }));
        } catch (error) {
            console.error('Error fetching asset details:', error);
            if (cachedAsset) {
                setAssetModal({ isOpen: true, userData: { ...cachedAsset, record: false }, loading: false });
            } else {
                setAssetModal(prev => ({ ...prev, loading: false }));
            }
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setErrors({});
        setApiError('');

        const data = {
            title: selectedEntry?.title || '',
            dueDate: selectedEntry?.dueDate || '',
            assetId: parseInt(selectedEntry?.assetId) || null,
            assignedTechnicianId: parseInt(selectedEntry?.assignedTechnicianId) || null,
            description: selectedEntry?.description || '',
            status: selectedEntry?.status || 'SUBMITTED',
            recallImageUrls: selectedEntry?.recallImageUrls || [],
            recallDocumentUrls: selectedEntry?.recallDocumentUrls || [],
            usageStatus: selectedEntry?.usageStatus || '',
            downtimeReasonId: selectedEntry?.usageStatus === 'OFFLINE' ? (selectedEntry?.downtimeReasonId || null) : null
        };

        if (selectedEntry?.id) {
            data.id = selectedEntry.id;
        }

        const newErrors = {};
        if (!selectedEntry?.assetId) {
            newErrors.assetId = t("shared.asset_is_required", "Asset is required");
        }
        if (!selectedEntry?.assignedTechnicianId) {
            newErrors.assignedTechnicianId = t("shared.technician_is_required", "Technician is required");
        }
        if (selectedEntry?.assetId && !selectedEntry?.usageStatus) {
            newErrors.usageStatus = t("shared.usage_status_is_required", "Usage status is required");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            let response;
            if (selectedEntry?.id) {
                response = await api.put(`recall/update`, data);
            } else {
                response = await api.post('recall/create', data);
            }

            const result = handleApiResponse(response);
            const notificationData = createNotificationFromApiResponse(result);

            if (result.success) {
                showSuccess(notificationData.message);
                addEditModal.close();
                fetchRecallRequests();
            } else {
                showError(notificationData.message);
            }
        } catch (error) {
            console.error('Error saving recall request:', error);
            setApiError(t("business.recall.failed_save", "Failed to save recall request"));
        }
    };

    const openImageModal = (images, index = 0) => {
        setCurrentImages(images || []);
        setSelectedImageIndex(index);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setCurrentImages([]);
        setSelectedImageIndex(0);
    };

    const openDocumentModal = (documents) => {
        setCurrentDocuments(documents || []);
        setShowDocumentModal(true);
    };

    const closeDocumentModal = () => {
        setShowDocumentModal(false);
        setCurrentDocuments([]);
    };

    const nextImage = () => {
        if (currentImages.length > 0) {
            setSelectedImageIndex((prev) =>
                prev === currentImages.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevImage = () => {
        if (currentImages.length > 0) {
            setSelectedImageIndex((prev) =>
                prev === 0 ? currentImages.length - 1 : prev - 1
            );
        }
    };


    useEffect(() => {
        fetchRecallRequests();
    }, []);

    return (
        <div className='page-content mt-4'>
            <NotificationAlert
                type={notification.type}
                message={notification.message}
                animated
            />

            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <PermissionGuard permission="maintenance:create">
                        <button
                            type='button'
                            className='btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake'
                            onClick={handleAdd}
                        >
                            <FeatherIcon icon='plus' />
                            {t("business.recall.add_recall", "Add Recall Request")}
                        </button>
                    </PermissionGuard>
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type='text'
                            placeholder={t("business.recall.search_recall", "Search Recall Requests...")}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t("business.recall.recall_requests", "Recall Requests")}</h3>
                    </div>
                </CardHeader>

                <CardBody>
                    {apiError && (
                        <NotificationAlert type="error" message={apiError} />
                    )}

                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    background: "white",
                                    zIndex: 1,
                                }}>
                                <tr>
                                    <th>#</th>
                                    <th>{t("shared.ticket_number", "Ticket Number")}</th>
                                    <th>{t("shared.asset", "Asset")}</th>
                                    <th>{t("shared.due_date", "Due Date")}</th>
                                    <th>{t("shared.status", "Status")}</th>
                                    <th>{t("shared.assigned_technician", "Assigned Technician")}</th>
                                    <th>{t("shared.delete", "Delete")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                {recallRequests?.map((request, ind) => (
                                    <motion.tr
                                        key={request?.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <th scope="row">{ind+1}</th>
                                        <td>
                                            {request.requestId ? (
                                                <span
                                                    className="text-primary fw-medium"
                                                    role="button"
                                                    onClick={() => handleWorkOrderClick(request)}
                                                    style={{ cursor: 'pointer' }}
                                                    title={t("shared.click_view_details", "Click to view details")}
                                                >
                                                    {request.requestId}
                                                </span>
                                            ) : (
                                                <span className="text-muted">{t("shared.n_a", "N/A")}</span>
                                            )}
                                        </td>
                                        <td>
                                            <span
                                                className="text-primary cursor-pointer hover-underline"
                                                onClick={() => handleAssetClick(request?.asset?.id, request?.asset)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {request?.asset?.assetName || t("shared.n_a", "N/A")}
                                            </span>
                                        </td>
                                            <td>{formatCreatedDate(request?.dueDate) || t("shared.n_a", "N/A")}</td>
                                            <td>
                                            {request.status ? (
                                                (() => {
                                                    const option = workstatusOptions(t).find(opt => opt.value === request.status) || {
                                                        label: request.status,
                                                        icon: 'help-circle',
                                                        colorClass: 'bg-secondary text-white',
                                                        borderColor: '#6c757d',
                                                    };
                                                    return (
                                                        <span
                                                            className={`badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 ${option.colorClass}`}
                                                            style={{ border: `1px solid ${option.borderColor}` }}
                                                        >
                                                            <FeatherIcon icon={option.icon} size={12} />
                                                            {option.label}
                                                        </span>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">{t("shared.n_a", "N/A")}</span>
                                            )}
                                            </td>
                                        <td>
                                            {request?.assignedTechnicianId?.id ? (
                                                <span className="badge bg-primary text-white" style={{ fontSize: '10px' }}>
                                                    <FeatherIcon icon="user" size={10} className="me-1" />
                                                    {request?.assignedTechnicianId?.name || t("shared.n_a", "N/A")}
                                                </span>
                                            ) : (
                                                <span className="text-muted small">{t("shared.not_assigned", "Not assigned")}</span>
                                            )}
                                        </td>
                                        <td>
                                            <PermissionGuard permission="maintenance:delete">
                                                <span className='text-danger' role='button' onClick={() => deleteModal.open(request)}>
                                                    <FeatherIcon icon="trash-2" />
                                                </span>
                                            </PermissionGuard>
                                        </td>
                                    </motion.tr>
                                ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {recallRequests.length === 0 && !loading && (
                        <div className="text-center py-4">
                            <p className="text-muted">{t("business.recall.no_recall_found", "No recall requests found")}</p>
                        </div>
                    )}

                    <LoadMoreButton
                        hasMore={hasMore}
                        loading={loading}
                        onLoadMore={onLoadMore}
                        dataLength={recallRequests.length}
                        loadMoreText={t("business.recall.load_more_recall", "Load More Recall Requests")}
                    />
                </CardBody>
            </Card>

            <Modal isOpen={addEditModal.isOpen} toggle={addEditModal.close} size="lg">
                <div className="modal-header">
                    <h5 className="modal-title">
                        {selectedEntry?.id ? t("business.recall.edit_recall", "Edit Recall Request") : t("business.recall.add_recall", "Add Recall Request")}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={addEditModal.close}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>
                <form onSubmit={handleSave}>
                    <div className="modal-body">
                        {errors.general && (
                            <Alert color="danger">{errors.general}</Alert>
                        )}
                        {apiError && (
                            <Alert color="danger">{apiError}</Alert>
                        )}

                        <Row className="mb-4">
                            <label htmlFor="title" className="col-sm-3 col-form-label">{t("shared.title", "Title")}</label>
                            <Col sm={9}>
                                <input
                                    type="text"
                                    name="title"
                                    id="title"
                                    className="form-control"
                                    value={selectedEntry?.title || ''}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, title: e.target.value})}
                                    placeholder={t("shared.enter_title", "Enter title...")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="dueDate" className="col-sm-3 col-form-label">{t("shared.due_date", "Due Date")} <span className="text-danger">*</span></label>
                            <Col sm={9}>
                                <DatePicker
                                    id="dueDate"
                                    selected={selectedEntry?.dueDate ? new Date(selectedEntry.dueDate) : null}
                                    onChange={(date) => {
                                        if (date) {
                                            const formattedDate = formatDateTimeForAPI(date);
                                            setSelectedEntry({
                                                ...selectedEntry,
                                                dueDate: formattedDate
                                            });
                                        }
                                    }}
                                    className="form-control"
                                    dateFormat="MMMM d, yyyy"
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    timeIntervals={15}
                                    timeCaption="time"
                                    isClearable
                                    placeholderText={t("shared.select_due_date", "Select due date")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="assetId" className="col-sm-3 col-form-label">{t("shared.asset", "Asset")} <span className="text-danger">*</span></label>
                            <Col sm={9}>
                                <div className="d-flex gap-2">
                                    <div className="flex-grow-1 position-relative">
                                        {selectedAssetDisplay ? (
                                            <div className="d-flex">
                                                <div
                                                    className="form-control d-flex align-items-center justify-content-between"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setSelectedAssetDisplay("");
                                                        setSelectedEntry({
                                                            ...selectedEntry,
                                                            asset: null,
                                                            assetId: '',
                                                            usageStatus: ''
                                                        });
                                                    }}
                                                >
                                                    <span>{selectedAssetDisplay}</span>
                                                    <FeatherIcon icon="x" size={16} className="text-muted" />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <Input
                                                    type="text"
                                                    id="assetSearch"
                                                    placeholder={t("shared.type_to_search_assets", "Type to search assets...")}
                                                    value={assetSearchTerm}
                                                    onChange={handleAssetSearchChange}
                                                    onFocus={() => {
                                                        if (assetSearchTerm.length > 0) {
                                                            setShowAssetDropdown(true);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        // Delay hiding dropdown to allow clicks on items
                                                        setTimeout(() => setShowAssetDropdown(false), 200);
                                                    }}
                                                    className={errors.assetId ? 'is-invalid' : ''}
                                                />
                                                {showAssetDropdown && assets.length > 0 && (
                                                    <div
                                                        className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                                                        style={{
                                                            zIndex: 1050,
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            top: '100%'
                                                        }}
                                                    >
                                                        {assets.map((asset) => (
                                                            <div
                                                                key={asset.id}
                                                                className="p-2 border-bottom cursor-pointer hover-bg-light d-flex align-items-center"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleAssetSelect(asset)}
                                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                            >
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-medium text-dark">{asset.assetName}</div>
                                                                    <small className="text-muted">
                                                                        {asset.serialNumber}
                                                                    </small>
                                                                </div>
                                                                <FeatherIcon icon="chevron-right" size={16} className="text-muted" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                                                    <div
                                                        className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                                                        style={{
                                                            zIndex: 1050,
                                                            top: '100%'
                                                        }}
                                                    >
                                                        {t("shared.no_assets_matching", "No assets found matching")} "{assetSearchTerm}"
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="align-self-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => setAssetFilterModal(true)}
                                            title={t("shared.filter_assets", "Filter Assets")}
                                            style={{ height: '38px' }}
                                        >
                                            <FeatherIcon icon="filter" size={16} />
                                        </button>
                                    </div>
                                </div>
                                {errors.assetId && <small className="text-danger">{errors.assetId}</small>}
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="assignedTechnicianId" className="col-sm-3 col-form-label">{t("shared.assigned_technician", "Assigned Technician")} <span className="text-danger">*</span></label>
                            <Col sm={9}>
                                <select
                                    name="assignedTechnicianId"
                                    id="assignedTechnicianId"
                                    className={`form-select ${errors.assignedTechnicianId ? 'is-invalid' : ''}`}
                                    value={selectedEntry?.assignedTechnicianId || ''}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, assignedTechnicianId: e.target.value})}
                                >
                                    <option value="">{t("shared.select_technician", "Select Technician")}</option>
                                    {technicians.map(tech => (
                                        <option key={tech.id} value={tech.id}>
                                            {tech.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.assignedTechnicianId && (
                                    <div className="invalid-feedback d-block">
                                        {errors.assignedTechnicianId}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="description" className="col-sm-3 col-form-label">{t("shared.description", "Description")}</label>
                            <Col sm={9}>
                                <textarea
                                    name="description"
                                    id="description"
                                    className="form-control"
                                    rows="3"
                                    value={selectedEntry?.description || ''}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, description: e.target.value})}
                                    placeholder={t("shared.enter_description", "Enter description")}
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="recallStatus" className="col-sm-3 col-form-label">{t("shared.status", "Status")}</label>
                            <Col sm={9}>
                                <select
                                    name="recallStatus"
                                    id="recallStatus"
                                    className="form-select"
                                    value={selectedEntry?.status || 'SUBMITTED'}
                                    onChange={(e) => setSelectedEntry({...selectedEntry, status: e.target.value})}
                                >
                                    {workstatusOptions(t).map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </Col>
                        </Row>

                        {selectedEntry?.assetId && (
                        <AssetUsageStatusFields
                            usageStatus={selectedEntry?.usageStatus}
                            downtimeReasonId={selectedEntry?.downtimeReasonId}
                            onChange={({ usageStatus, downtimeReasonId }) => setSelectedEntry({...selectedEntry, usageStatus, downtimeReasonId})}
                            errors={errors}
                            layout="horizontal"
                            required
                        />
                        )}

                        <Row className="mb-4">
                            <label htmlFor="imagesUpload" className="col-sm-3 col-form-label">{t("shared.images", "Images")}</label>
                            <Col sm={9}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileChange(e, 'images')}
                                    className="form-control"
                                    id="imagesUpload"
                                    multiple
                                />
                                {selectedEntry?.recallImageUrls && selectedEntry.recallImageUrls.length > 0 && (
                                    <div className="mt-3">
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedEntry.recallImageUrls.map((imageUrl, index) => (
                                                <div key={index} className="position-relative">
                                                    <img
                                                        src={imageUrl}
                                                        alt={`${t("shared.preview", "Preview")} ${index + 1}`}
                                                        className="img-thumbnail"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                                        style={{ transform: 'translate(50%, -50%)', padding: '2px 6px' }}
                                                        onClick={() => removeImage(index)}
                                                    >
                                                        <FeatherIcon icon="x" size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row className="mb-4">
                            <label htmlFor="documentsUpload" className="col-sm-3 col-form-label">{t("shared.documents", "Documents")}</label>
                            <Col sm={9}>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                                    onChange={(e) => handleFileChange(e, 'documents')}
                                    className="form-control"
                                    id="documentsUpload"
                                    multiple
                                />
                                {selectedEntry?.recallDocumentUrls && selectedEntry.recallDocumentUrls.length > 0 && (
                                    <div className="mt-3">
                                        <small className="text-muted">{t("shared.current_documents", "Current Documents:")}</small>
                                        <div className="mt-1">
                                            {selectedEntry.recallDocumentUrls.map((docUrl, index) => {
                                                const isPdf = docUrl?.toLowerCase().endsWith(".pdf");
                                                const isImage = ["jpg", "jpeg", "png", "gif"].some(ext =>
                                                    docUrl?.toLowerCase().endsWith(ext)
                                                );
                                                const fileName = docUrl?.split("/").pop() || `${t("shared.document", "Document")} ${index + 1}`;

                                                return (
                                                    <div key={index} className="d-flex align-items-center justify-content-between p-2 border rounded mb-1">
                                                        <div className="d-flex align-items-center">
                                                            <FeatherIcon
                                                                icon={isPdf ? "file-text" : isImage ? "image" : "file"}
                                                                size="16"
                                                                className="text-primary me-2"
                                                            />
                                                            <small className="text-truncate" style={{ maxWidth: '200px' }}>
                                                                {fileName}
                                                            </small>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-1">
                                                            <a
                                                                href={docUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-primary btn-sm"
                                                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                                            >
                                                                {t("shared.view", "View")}
                                                            </a>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                style={{ fontSize: '10px', padding: '2px 6px' }}
                                                                onClick={() => removeDocument(index)}
                                                            >
                                                                <FeatherIcon icon="x" size={12} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <Button type="button" color="secondary" onClick={addEditModal.close}>
                            {t("shared.cancel", "Cancel")}
                        </Button>
                        <Button type="submit" color="primary">
                            {selectedEntry?.id ? t("shared.update", "Update") : t("shared.save", "Save")}
                        </Button>
                    </div>
                </form>
            </Modal>

            <ConfirmDeleteModal
                isOpen={deleteModal.isOpen}
                toggle={deleteModal.close}
                onConfirm={handleDelete}
                message={t("business.recall.delete_confirmation", "Are you sure you want to delete this recall request? This action cannot be undone.")}
            />

            <AnimatePresence>
                {showImageModal && currentImages.length > 0 && (
                    <Modal
                        isOpen={showImageModal}
                        toggle={closeImageModal}
                        size="lg"
                        centered
                        className="image-modal"
                        backdrop="static"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ type: "spring", stiffness: 300, damping: 25 }}
                            className="modal-content border-0"
                            style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
                        >
                            <div className="modal-header border-0 bg-transparent text-white">
                                <h5 className="modal-title">
                                    {t("shared.image_x_of_y", "Image {{x}} of {{y}}", { x: selectedImageIndex + 1, y: currentImages.length })}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={closeImageModal}
                                    aria-label={t("shared.close", "Close")}
                                />
                            </div>

                            <div className="modal-body p-0 d-flex align-items-center justify-content-center position-relative">
                                {currentImages.length > 1 && (
                                    <>
                                        <button
                                            className="btn btn-outline-light position-absolute start-0 ms-3 rounded-circle"
                                            style={{ zIndex: 10, width: '50px', height: '50px' }}
                                            onClick={prevImage}
                                        >
                                            <i className="bx bx-chevron-left fs-4"></i>
                                        </button>
                                        <button
                                            className="btn btn-outline-light position-absolute end-0 me-3 rounded-circle"
                                            style={{ zIndex: 10, width: '50px', height: '50px' }}
                                            onClick={nextImage}
                                        >
                                            <i className="bx bx-chevron-right fs-4"></i>
                                        </button>
                                    </>
                                )}

                                <motion.img
                                    key={selectedImageIndex}
                                    src={currentImages[selectedImageIndex]}
                                    alt={`Image ${selectedImageIndex + 1}`}
                                    className="img-fluid"
                                    style={{
                                        maxHeight: '70vh',
                                        maxWidth: '100%',
                                        objectFit: 'contain'
                                    }}
                                    initial={{ opacity: 0, x: 100 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -100 }}
                                    transition={{ duration: 0.3 }}
                                    onError={(e) => {
                                    }}
                                />
                            </div>

                            {currentImages.length > 1 && (
                                <div className="modal-footer border-0 bg-transparent d-flex justify-content-center">
                                    <div className="d-flex gap-2">
                                        {currentImages.map((_, index) => (
                                            <button
                                                key={index}
                                                className={`btn rounded-circle p-1 ${
                                                    index === selectedImageIndex
                                                        ? 'btn-light'
                                                        : 'btn-outline-light'
                                                }`}
                                                style={{ width: '12px', height: '12px' }}
                                                onClick={() => setSelectedImageIndex(index)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Document Viewer Modal */}
            <Modal
                isOpen={showDocumentModal}
                toggle={closeDocumentModal}
                size="lg"
            >
                <div className="modal-header">
                    <h5 className="modal-title">{t("business.recall.recall_documents", "Recall Documents")}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={closeDocumentModal}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>
                <div className="modal-body">
                    {currentDocuments.length > 0 ? (
                        <Row>
                            {currentDocuments.map((docUrl, index) => {
                                const isPdf = docUrl?.toLowerCase().endsWith(".pdf");
                                const isImage = ["jpg", "jpeg", "png", "gif"].some(ext =>
                                    docUrl?.toLowerCase().endsWith(ext)
                                );

                                return (
                                    <Col md={6} className="mb-4" key={index}>
                                        <div className="card border-0 shadow-sm h-100">
                                            <div className="card-body text-center">
                                                <FeatherIcon
                                                    icon={isPdf ? "file-text" : isImage ? "image" : "file"}
                                                    size="48"
                                                    className="text-primary mb-3"
                                                />
                                                <h6 className="mb-2">{t("shared.document", "Document")} {index + 1}</h6>
                                                <small className="text-muted d-block mb-3 text-truncate">
                                                    {docUrl?.split("/").pop()}
                                                </small>
                                                <a
                                                    href={docUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-primary"
                                                >
                                                    {isPdf ? t("shared.view_pdf", "View PDF") : isImage ? t("shared.view_image", "View Image") : t("shared.download", "Download")}
                                                </a>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <div className="text-center py-4">
                            <FeatherIcon icon="file" size="48" className="text-muted mb-2" />
                            <p className="text-muted">{t("shared.no_documents_available", "No documents available")}</p>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeDocumentModal}
                    >
                        {t("shared.close", "Close")}
                    </button>
                </div>
            </Modal>

            {/* Work Order Details Modal */}
            <WorkOrderDetailsModal
                isOpen={workOrderModal.isOpen}
                toggle={workOrderModal.close}
                workOrder={workOrderModal.data}
                workType="recall"
                onEdit={(workOrder) => {
                    workOrderModal.close();
                    handleEdit(workOrder);
                }}
            />

            {/* Asset Modal */}
            <AssetModal
                isOpen={assetModal.isOpen}
                toggle={() => setAssetModal(prev => ({ ...prev, isOpen: false }))}
                userData={assetModal.userData}
                loading={assetModal.loading}
                formatCreatedDate={formatCreatedDate}
            />

            {/* Asset Filter Modal */}
            <Modal
                isOpen={assetFilterModal}
                toggle={() => setAssetFilterModal(false)}
                centered
            >
                <div className="modal-header">
                    <h5 className="modal-title">{t("shared.filter_assets", "Filter Assets")}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAssetFilterModal(false)}
                        aria-label={t("shared.close", "Close")}
                    />
                </div>

                <div className="modal-body">
                    <Row className="mb-3">
                        <Col md={12}>
                            <label htmlFor="filterAssetType" className="form-label">
                                {t('shared.asset_type', 'Asset Type')}
                            </label>
                            <select
                                id="filterAssetType"
                                className="form-select"
                                value={assetFilters.assetTypeId}
                                onChange={(e) =>
                                    setAssetFilters({ ...assetFilters, assetTypeId: e.target.value })
                                }
                            >
                                <option value="">{t('shared.all_asset_types', 'All Asset Types')}</option>
                                {assetTypes.map(at => (
                                    <option key={at.id} value={at.id}>{at.name}</option>
                                ))}
                            </select>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <label htmlFor="assetFilterLocation" className="form-label">
                                {t("shared.location", "Location")}
                            </label>
                            <select
                                id="assetFilterLocation"
                                className="form-select"
                                value={assetFilters.location}
                                onChange={(e) =>
                                    setAssetFilters({ ...assetFilters, location: e.target.value })
                                }
                            >
                                <option value="">{t("shared.all_locations", "All Locations")}</option>
                                {locations?.map((location) => (
                                    <option key={location.id} value={location.locationName}>
                                        {location.locationName}
                                    </option>
                                ))}
                            </select>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={12}>
                            <label htmlFor="assetFilterManufacturer" className="form-label">
                                {t("shared.manufacturer", "Manufacturer")}
                            </label>
                            <select
                                id="assetFilterManufacturer"
                                className="form-select"
                                value={assetFilters.manufacturer}
                                onChange={(e) =>
                                    setAssetFilters({ ...assetFilters, manufacturer: e.target.value })
                                }
                            >
                                <option value="">{t("shared.all_manufacturers", "All Manufacturers")}</option>
                                {manufacturers?.map((manufacturer) => (
                                    <option key={manufacturer} value={manufacturer}>
                                        {manufacturer}
                                    </option>
                                ))}
                            </select>
                        </Col>
                    </Row>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setAssetFilters({
                                assetTypeId: '',
                                location: '',
                                manufacturer: ''
                            });
                            setAssetFilterModal(false);
                        }}
                    >
                        {t("shared.clear_filters", "Clear Filters")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => setAssetFilterModal(false)}
                    >
                        {t("shared.apply_filters", "Apply Filters")}
                    </button>
                </div>
            </Modal>

        </div>
    );
};

export default Recall;